import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Flame, Calendar, MapPin, ArrowLeft, Send, Loader2, CheckCircle, ShoppingBag, Tag, CreditCard, Banknote, ExternalLink } from "lucide-react";
import DOMPurify from "dompurify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const round2 = (n) => Math.round(n * 100) / 100;

const FONT_OPTIONS = [
  { value: "'Dancing Script', cursive", label: "Dancing Script", google: "Dancing+Script" },
  { value: "Comic Sans MS", label: "Comic Sans" },
  { value: "'Permanent Marker', cursive", label: "Permanent Marker", google: "Permanent+Marker" },
  { value: "'Alfa Slab One', serif", label: "Alfa Slab One", google: "Alfa+Slab+One" },
];

// Load Google Fonts
const GOOGLE_FONT_URL = "https://fonts.googleapis.com/css2?" + FONT_OPTIONS.filter(f => f.google).map(f => `family=${f.google}`).join("&") + "&display=swap";
if (typeof document !== "undefined" && !document.querySelector(`link[href*="fonts.googleapis.com"]`)) {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = GOOGLE_FONT_URL;
  document.head.appendChild(link);
}

const DEFAULT_FIELDS = [
  { key: "name", label: "Full Name", visible: true, required: true, type: "text" },
  { key: "email", label: "Email", visible: true, required: true, type: "email" },
  { key: "phone", label: "Phone", visible: true, required: false, type: "phone" },
  { key: "address", label: "Address", visible: true, required: false, type: "text" },
  { key: "customization_text", label: "Engraving Text", visible: true, required: false, type: "text" },
  { key: "customer_notes", label: "Special Notes", visible: true, required: false, type: "textarea" },
];

function PhoneInput({ value, onChange, required, label, isEmpty }) {
  const [parts, setParts] = useState(() => {
    const clean = (value || "").replace(/\D/g, "");
    return [clean.slice(0, 3), clean.slice(3, 6), clean.slice(6, 10)];
  });
  const ref2 = useRef(null);
  const ref3 = useRef(null);

  const handleChange = (idx, val) => {
    const digits = val.replace(/\D/g, "");
    const maxLen = idx === 2 ? 4 : 3;
    const clamped = digits.slice(0, maxLen);
    const next = [...parts];
    next[idx] = clamped;
    setParts(next);
    onChange(next.join("") ? `${next[0]}-${next[1]}-${next[2]}` : "");
    if (clamped.length === maxLen) {
      if (idx === 0) ref2.current?.focus();
      if (idx === 1) ref3.current?.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && parts[idx] === "") {
      if (idx === 2) ref2.current?.focus();
      if (idx === 1) {
        const firstInput = e.target.parentElement?.querySelector('input');
        firstInput?.focus();
      }
    }
  };

  return (
    <div className={`rounded-sm p-2.5 -m-1 transition-colors ${isEmpty ? "bg-red-50/70" : ""}`}>
      <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-1">
        {label}{required && " *"}
      </label>
      <div className="flex items-center gap-1.5">
        <input
          className="brutal-input w-[72px] text-center tracking-widest"
          placeholder="000"
          maxLength={3}
          value={parts[0]}
          onChange={e => handleChange(0, e.target.value)}
          onKeyDown={e => handleKeyDown(0, e)}
          data-testid="phone-part-1"
          inputMode="numeric"
        />
        <span className="font-heading font-bold text-lg text-zinc-400">-</span>
        <input
          ref={ref2}
          className="brutal-input w-[72px] text-center tracking-widest"
          placeholder="000"
          maxLength={3}
          value={parts[1]}
          onChange={e => handleChange(1, e.target.value)}
          onKeyDown={e => handleKeyDown(1, e)}
          data-testid="phone-part-2"
          inputMode="numeric"
        />
        <span className="font-heading font-bold text-lg text-zinc-400">-</span>
        <input
          ref={ref3}
          className="brutal-input w-[88px] text-center tracking-widest"
          placeholder="0000"
          maxLength={4}
          value={parts[2]}
          onChange={e => handleChange(2, e.target.value)}
          onKeyDown={e => handleKeyDown(2, e)}
          data-testid="phone-part-3"
          inputMode="numeric"
        />
      </div>
    </div>
  );
}

function FontPicker({ field, value, onChange, isEmpty }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className={`relative rounded-sm p-2.5 -m-1 transition-colors ${isEmpty ? "bg-red-50/70" : ""}`} data-testid={`event-field-${field.key}`}>
      <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-1">
        {field.label}{field.required ? " *" : ""}
      </label>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="brutal-input w-full text-left flex items-center justify-between"
        style={{ fontFamily: value || "inherit" }}
        data-testid={`event-field-${field.key}-select`}
      >
        <span className={value ? "text-black" : "text-zinc-400"}>
          {value ? FONT_OPTIONS.find(f => f.value === value)?.label || value : "Select a font..."}
        </span>
        <svg className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 border-2 border-black bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] max-h-80 overflow-y-auto" data-testid={`font-dropdown-${field.key}`}>
          {FONT_OPTIONS.map(fo => (
            <button
              key={fo.value}
              type="button"
              onClick={() => { onChange(fo.value); setOpen(false); }}
              className={`w-full text-left px-4 py-3 border-b border-zinc-100 last:border-0 transition-colors ${value === fo.value ? "bg-[#FFD600]" : "hover:bg-zinc-50"}`}
              data-testid={`font-option-${fo.value.replace(/\s/g, "-")}`}
            >
              <span className="text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-400 block">{fo.label}</span>
              <span className="text-xl block mt-0.5" style={{ fontFamily: fo.value }}>Burn on Site Laser Engraving</span>
            </button>
          ))}
        </div>
      )}
      {value && (
        <p className="mt-2 text-lg border-2 border-dashed border-zinc-200 p-3 text-center" style={{ fontFamily: value }} data-testid={`font-preview-${field.key}`}>
          Burn on Site Laser Engraving
        </p>
      )}
    </div>
  );
}


export default function EventLandingPage() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [product, setProduct] = useState(null);
  const [engraving, setEngraving] = useState(null);
  const [error, setError] = useState("");

  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [fields, setFields] = useState([]);

  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("online");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/events/${eventId}/landing`);
        setEvent(res.data.event);
        setProduct(res.data.product);
        setEngraving(res.data.engraving);
        const fieldsCfg = res.data.event.landing_fields?.length ? res.data.event.landing_fields : DEFAULT_FIELDS;
        const visibleFields = fieldsCfg.filter(f => f.visible);
        setFields(visibleFields);
        const initialForm = {};
        visibleFields.forEach(f => { initialForm[f.key] = ""; });
        setForm(initialForm);
        // Auto-apply promo from URL
        const urlPromo = searchParams.get("promo");
        if (urlPromo) {
          setPromoCode(urlPromo);
          try {
            const promoRes = await axios.post(`${API}/promo-codes/apply`, { code: urlPromo });
            setPromoApplied(promoRes.data);
          } catch (err) {
            setPromoError(err.response?.data?.detail || "Invalid promo code");
          }
        }
      } catch (e) {
        setError(e.response?.data?.detail || "Event not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, searchParams]);

  const isFormValid = () => {
    return fields.every(f => {
      if (f.type === "heading") return true;
      if (!f.required) return true;
      const val = (form[f.key] || "").trim();
      if (!val) return false;
      if (f.type === "phone") {
        const digits = val.replace(/\D/g, "");
        return digits.length === 10;
      }
      return true;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;
    setSubmitting(true);
    try {
      const payload = {};
      fields.forEach(f => { if (f.type !== "heading") payload[f.key] = (form[f.key] || "").trim(); });
      if (promoApplied) payload.promo_code = promoCode;
      payload.payment_method = paymentMethod;
      const res = await axios.post(`${API}/events/${eventId}/orders`, payload);
      setOrderId(res.data.id);
      setSubmitted(true);

      if (paymentMethod === "online" && event.landing_payment_mode === "stripe" && res.data.status === "pending_payment") {
        const sessionRes = await axios.post(`${API}/payments/create-session`, {
          order_id: res.data.id,
          origin_url: window.location.origin,
        });
        if (sessionRes.data.url) {
          window.location.href = sessionRes.data.url;
          return;
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit order");
    } finally {
      setSubmitting(false);
    }
  };

  const price = event?.landing_price != null && event.landing_price !== ""
    ? parseFloat(event.landing_price)
    : (product?.price || 0) + (engraving?.price || 0);

  const discount = promoApplied
    ? promoApplied.promo_type === "material_only" ? price : round2(price * (promoApplied.discount_percent || 0) / 100)
    : 0;
  const total = Math.max(0, round2(price - discount));
  const isFree = event?.landing_payment_mode === "none";
  const hasStripe = event?.landing_payment_mode === "stripe";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#FF3366]" />
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-4">
        <div className="brutal-card p-8 max-w-md w-full text-center">
          <Flame size={40} className="mx-auto mb-4 text-[#FF3366]" strokeWidth={2.5} />
          <h2 className="font-heading text-xl font-black uppercase mb-2">Event Not Found</h2>
          <p className="text-zinc-500 text-sm mb-6">{error}</p>
          <button onClick={() => navigate("/")} className="brutal-btn text-sm" data-testid="go-home-btn">
            <ArrowLeft size={14} className="inline mr-2" /> Go to Main Site
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-4">
        <div className="brutal-card p-8 max-w-md w-full text-center" data-testid="event-order-success">
          <CheckCircle size={48} className="mx-auto mb-4 text-green-500" strokeWidth={2} />
          <h2 className="font-heading text-2xl font-black uppercase mb-2">You're All Set!</h2>
          <p className="text-zinc-500 text-sm mb-2">Your order has been placed at <strong>{event.name}</strong>.</p>
          <p className="font-mono text-xs text-zinc-400 mb-6">Order #{orderId?.slice(0, 8)}</p>
          {product && <p className="text-sm mb-6"><strong>{product.name}</strong>{isFree ? " — Included with event" : ` — $${price.toFixed(2)}`}</p>}
          <div className="space-y-3">
            <button onClick={() => navigate(`/track?type=order_id&q=${orderId}${event.show_home_button === false ? '&hideHome=1' : ''}`)} className="brutal-btn w-full text-sm" data-testid="track-order-btn">Track Your Order</button>
            {event.show_home_button !== false && (
              <button onClick={() => navigate("/")} className="brutal-btn brutal-btn-outline w-full text-sm" data-testid="back-home-btn">
                <ArrowLeft size={14} className="inline mr-2" /> Visit Main Site
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const isFieldEmpty = (field) => {
    if (!field.required) return false;
    const val = (form[field.key] || "").trim();
    if (!val) return true;
    if (field.type === "phone") return val.replace(/\D/g, "").length < 10;
    return false;
  };

  const renderField = (field) => {
    if (field.type === "heading") {
      return (
        <div key={field.key} className="pt-3 pb-1 border-b-2 border-black" data-testid={`event-field-${field.key}`}>
          <h3 className="font-heading font-black text-sm uppercase tracking-wider text-[#FF3366]">{field.label}</h3>
        </div>
      );
    }
    if (field.type === "boolean") {
      const val = form[field.key] || "";
      const empty = field.required && !val;
      return (
        <div key={field.key} className={`rounded-sm p-2.5 -m-1 transition-colors ${empty ? "bg-red-50/70" : ""}`} data-testid={`event-field-${field.key}`}>
          <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">
            {field.label}{field.required ? " *" : ""}
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setForm(f => ({ ...f, [field.key]: "Yes" }))}
              className={`flex-1 text-xs px-4 py-3 border-2 border-black font-heading font-bold uppercase transition-all ${val === "Yes" ? "bg-green-500 text-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]" : "bg-white hover:bg-zinc-50"}`}
              data-testid={`event-field-${field.key}-yes`}>
              Yes
            </button>
            <button type="button" onClick={() => setForm(f => ({ ...f, [field.key]: "No" }))}
              className={`flex-1 text-xs px-4 py-3 border-2 border-black font-heading font-bold uppercase transition-all ${val === "No" ? "bg-red-400 text-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]" : "bg-white hover:bg-zinc-50"}`}
              data-testid={`event-field-${field.key}-no`}>
              No
            </button>
          </div>
        </div>
      );
    }
    if (field.type === "dropdown") {
      const empty = isFieldEmpty(field);
      return (
        <div key={field.key} className={`rounded-sm p-2.5 -m-1 transition-colors ${empty ? "bg-red-50/70" : ""}`} data-testid={`event-field-${field.key}`}>
          <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-1">
            {field.label}{field.required ? " *" : ""}
          </label>
          <select
            className="brutal-input w-full"
            value={form[field.key] || ""}
            onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
            data-testid={`event-field-${field.key}-select`}
          >
            <option value="">Select...</option>
            {(field.options || []).filter(o => o).map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }
    if (field.type === "font") {
      return (
        <FontPicker
          key={field.key}
          field={field}
          value={form[field.key] || ""}
          onChange={val => setForm(f => ({ ...f, [field.key]: val }))}
          isEmpty={isFieldEmpty(field)}
        />
      );
    }
    if (field.type === "phone") {
      return (
        <PhoneInput
          key={field.key}
          value={form[field.key] || ""}
          onChange={val => setForm(f => ({ ...f, [field.key]: val }))}
          required={field.required}
          label={field.label}
          isEmpty={isFieldEmpty(field)}
        />
      );
    }
    if (field.type === "textarea") {
      const empty = isFieldEmpty(field);
      return (
        <div key={field.key} className={`rounded-sm p-2.5 -m-1 transition-colors ${empty ? "bg-red-50/70" : ""}`}>
          <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-1">
            {field.label}{field.required ? " *" : ""}
          </label>
          <textarea
            className="brutal-input w-full min-h-[60px] resize-none"
            placeholder={field.label}
            value={form[field.key] || ""}
            onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
            required={field.required}
            data-testid={`event-field-${field.key}`}
          />
        </div>
      );
    }
    const empty = isFieldEmpty(field);
    return (
      <div key={field.key} className={`rounded-sm p-2.5 -m-1 transition-colors ${empty ? "bg-red-50/70" : ""}`}>
        <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-1">
          {field.label}{field.required ? " *" : ""}
        </label>
        <input
          type={field.type === "email" ? "email" : "text"}
          className="brutal-input w-full"
          placeholder={field.label}
          value={form[field.key] || ""}
          onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
          required={field.required}
          data-testid={`event-field-${field.key}`}
        />
      </div>
    );
  };

  const bgUrl = event?.landing_background_url
    ? (event.landing_background_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${event.landing_background_url}` : event.landing_background_url)
    : null;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <div className="bg-black text-white border-b-2 border-black">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Flame size={22} strokeWidth={2.5} className="text-[#FF3366]" />
            <span className="font-heading font-black text-sm uppercase tracking-wider">Burn On Site</span>
          </div>
          {event?.show_home_button !== false && (
            <button onClick={() => navigate("/")} className="text-xs text-zinc-400 hover:text-white font-heading font-bold uppercase flex items-center gap-1 transition-colors" data-testid="nav-home-btn">
              <ArrowLeft size={14} strokeWidth={2.5} /> Main Site
            </button>
          )}
        </div>
      </div>

      {/* Background Hero */}
      {bgUrl && (
        <div className="relative w-full h-48 md:h-64 overflow-hidden border-b-2 border-black" data-testid="event-bg-hero">
          <img src={bgUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60" />
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 py-8" style={bgUrl ? { marginTop: "-2rem", position: "relative", zIndex: 10 } : undefined}>
        {/* Event Header */}
        <div className="brutal-card overflow-hidden mb-8" data-testid="event-header">
          <div className="bg-[#FF3366] text-white p-6 border-b-2 border-black">
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={20} strokeWidth={2.5} />
              <h1 className="font-heading text-2xl font-black uppercase">{event.name}</h1>
            </div>
            <div className="flex flex-wrap gap-4 text-sm opacity-90">
              {event.event_date && (
                <span>{new Date(event.event_date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</span>
              )}
              {event.location && <span className="flex items-center gap-1"><MapPin size={14} /> {event.location}</span>}
              {event.link && (
                <a href={event.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 underline hover:opacity-100 transition-opacity" data-testid="event-link">
                  <ExternalLink size={14} /> Event Details
                </a>
              )}
            </div>
          </div>
          {event.landing_description && (
            <div className="p-6 text-sm text-zinc-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(event.landing_description) }} data-testid="event-description" />
          )}
        </div>

        {/* Product Info */}
        {product && (
          <div className="brutal-card p-6 mb-8" data-testid="event-product-info">
            <div className="flex items-center gap-6">
              {product.image_url && (
                <img src={product.image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${product.image_url}` : product.image_url}
                  alt={product.name} className="w-24 h-24 object-contain border-2 border-black bg-white p-1" />
              )}
              <div className="flex-1">
                <h3 className="font-heading font-bold text-lg uppercase">{product.name}</h3>
                {engraving && <p className="text-xs text-zinc-500 mt-1">{engraving.name} engraving</p>}
                {product.description && <p className="text-xs text-zinc-400 mt-1">{product.description}</p>}
              </div>
              <div className="text-right">
                {isFree ? (
                  <span className="font-heading font-black text-lg text-green-600">FREE</span>
                ) : discount > 0 ? (
                  <div>
                    <span className="font-heading text-sm text-zinc-400 line-through block">${price.toFixed(2)}</span>
                    <span className="font-heading font-black text-2xl text-green-600">${total.toFixed(2)}</span>
                  </div>
                ) : (
                  <span className="font-heading font-black text-2xl text-[#FF3366]">${price.toFixed(2)}</span>
                )}
              </div>
            </div>
            {promoApplied && (
              <div className="mt-3 flex items-center gap-2 bg-green-50 border-2 border-green-500 px-4 py-2" data-testid="promo-applied-badge">
                <Tag size={14} strokeWidth={2.5} className="text-green-600" />
                <span className="text-xs font-heading font-bold uppercase text-green-700">
                  {promoApplied.promo_type === "material_only" ? "100% Off" : `${promoApplied.discount_percent}% Off`} — Code: {promoCode}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Order Form */}
        {fields.length > 0 ? (
          <form onSubmit={handleSubmit} className="brutal-card p-6" data-testid="event-order-form">
            <h2 className="font-heading text-lg font-black uppercase mb-6 flex items-center gap-2">
              <ShoppingBag size={18} strokeWidth={2.5} className="text-[#FF3366]" />
              Enter Your Information
            </h2>

            <div className="space-y-4">
              {fields.map(field => renderField(field))}
            </div>

            {/* Promo Code Input (if not already applied from URL) */}
            {hasStripe && !promoApplied && (
              <div className="mt-5 border-t-2 border-zinc-200 pt-5">
                <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Have a promo code?</label>
                <div className="flex gap-2">
                  <input className="brutal-input flex-1" placeholder="Enter code" value={promoCode} onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }} data-testid="promo-code-input" />
                  <button type="button" disabled={!promoCode.trim() || applyingPromo} onClick={async () => {
                    setApplyingPromo(true); setPromoError("");
                    try { const res = await axios.post(`${API}/promo-codes/apply`, { code: promoCode.trim() }); setPromoApplied(res.data); }
                    catch (err) { setPromoError(err.response?.data?.detail || "Invalid promo code"); }
                    finally { setApplyingPromo(false); }
                  }} className="brutal-btn text-xs px-4 disabled:opacity-50" data-testid="apply-promo-btn">
                    {applyingPromo ? <Loader2 size={14} className="animate-spin" /> : <><Tag size={14} /> Apply</>}
                  </button>
                </div>
                {promoError && <p className="text-red-500 text-xs font-bold mt-1" data-testid="promo-error">{promoError}</p>}
              </div>
            )}

            {/* Payment Method Selection */}
            {hasStripe && !isFree && (
              <div className="mt-5 border-t-2 border-zinc-200 pt-5">
                <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-3">Payment Method</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setPaymentMethod("online")}
                    className={`flex-1 flex items-center justify-center gap-2 text-xs px-4 py-3.5 border-2 border-black font-heading font-bold uppercase transition-all ${paymentMethod === "online" ? "bg-[#00E5FF] text-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]" : "bg-white hover:bg-zinc-50"}`}
                    data-testid="pay-online-btn">
                    <CreditCard size={16} strokeWidth={2.5} /> Pay Online
                  </button>
                  <button type="button" onClick={() => setPaymentMethod("in_person")}
                    className={`flex-1 flex items-center justify-center gap-2 text-xs px-4 py-3.5 border-2 border-black font-heading font-bold uppercase transition-all ${paymentMethod === "in_person" ? "bg-[#FFD600] text-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]" : "bg-white hover:bg-zinc-50"}`}
                    data-testid="pay-in-person-btn">
                    <Banknote size={16} strokeWidth={2.5} /> Pay in Person
                  </button>
                </div>
              </div>
            )}

            {error && <p className="text-red-500 text-xs font-bold mt-4" data-testid="event-form-error">{error}</p>}

            <button type="submit" disabled={submitting || !isFormValid()}
              className="brutal-btn w-full mt-6 text-sm py-4 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="event-submit-btn">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting...</> :
                <><Send size={16} strokeWidth={2.5} /> {isFree ? "Place Order" : paymentMethod === "in_person" ? `Place Order — Pay $${total.toFixed(2)} in Person` : `Place Order — $${total.toFixed(2)}`}</>}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="brutal-card p-6 text-center" data-testid="event-order-form">
            <ShoppingBag size={24} strokeWidth={2.5} className="text-[#FF3366] mx-auto mb-3" />
            <p className="text-sm text-zinc-500 mb-4">No additional information needed.</p>
            {error && <p className="text-red-500 text-xs font-bold mb-4" data-testid="event-form-error">{error}</p>}
            <button type="submit" disabled={submitting}
              className="brutal-btn w-full text-sm py-4 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="event-submit-btn">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting...</> : <><Send size={16} strokeWidth={2.5} /> {isFree ? "Place Order" : `Place Order — $${price.toFixed(2)}`}</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
